<template>
  <fieldset
    :ref="generateSafeId(fieldQuestionId, 'wrapper')"
    class="RMPE-DynamicField"
    :class="{ 'RMPE-HasError': fieldHasError }"
  >
    <component
      :is="layout.type"
      :image="layout.image"
      :video="layout.video"
    >
      <div class="field">
        <legend
          class="label RMPE-DynamicField-Label"
        >
          <span
            :class="{'RMPE-Required': question.required}"
            v-html="question.formattedLabel"
          />
        </legend>

        <HintMessage :hint="question.formattedDescription" />

        <MediaMiddlePartial :layout="layout" />

        <ul class="RMPE-MultiChoiceImages">
          <div
            v-for="(option, index) in options"
            :key="option.id"
            class="RMPE-MultiChoiceImage"
            :class="{'isSelected': isSelected(option.id)}"
          >
            <div
              v-if="isSelected(option.id)"
              class="RMPE-MultiChoiceImage-Icon"
            >
              <font-awesome-layers>
                <font-awesome-icon :icon="maskIcon" />
                <font-awesome-icon
                  :icon="activeIcon"
                  transform="shrink-6"
                />
              </font-awesome-layers>
            </div>
            <label
              :id="generateSafeId(fieldQuestionId, option.id, 'label')"
              :ref="index === 0 ? 'auto-focus-target' : null"
              :for="generateSafeId(fieldQuestionId, option.id)"
              tabindex="0"
              @keydown.space.prevent="$emit('change', option.id)"
            >

              <div
                class="RMPE-MultiChoiceImage-Image"
                :style="{ backgroundImage: `url(${option.image})`}"
              />

              <input
                :id="generateSafeId(fieldQuestionId, option.id)"
                :ref="generateSafeId(fieldQuestionId, option.id, 'ref')"
                :value="option.id"
                :checked="fieldValue === option.id"
                :name="question.id"
                type="radio"
                autocomplete="no"
                @click="selectImage(option.id)"
              >
              <span class="label RMPE-MultiChoiceImage-Label">
                <kbd
                  v-if="shouldShowKeyboardHint"
                  class="RMPE-KeyboardKey"
                >
                  {{ getKeyboardKey(index) }}
                </kbd>
                <span>
                  {{ option.label }}
                </span>
              </span>
            </label>
          </div>
        </ul>

        <div class="control" />

        <ErrorMessage
          :field-has-error="fieldHasError"
          :errors="error.errors"
          :is-blurred="isBlurred"
        />

        <div class="RMPE-DynamicForm-SubmitContainer">
          <button
            :disabled="isNextButtonDisabled"
            class="button is-primary RMPE-CustomSubmit"
            @click="goToNextQuestion"
          >
            {{ question.goToNextQuestionButtonLabel }}
          </button>
        </div>
      </div>
    </component>
  </fieldset>
</template>

<script>
import { FontAwesomeLayers } from '@fortawesome/vue-fontawesome';
import { faCheck, faCircle } from '@fortawesome/free-solid-svg-icons';
import DynamicFieldMixin from '../../mixins/dynamic-field-mixin';
import KeyboardShortcutMixin from '../../mixins/keyboard-shortcut-mixin';
import MultiChoiceImageFieldMixin from '../common/multi-choice-image-field';

export default {
  components: {
    FontAwesomeLayers,
  },

  mixins: [DynamicFieldMixin, KeyboardShortcutMixin, MultiChoiceImageFieldMixin],

  data() {
    return {
      isBlurred: false,
      activeIcon: faCheck,
      maskIcon: faCircle,
      selectedOptions: [],
    };
  },
  computed: {
    settings() {
      return {
        maxNumberOfSelections: this.question.selectedQuantity,
        isMultiSelect: this.question.multiSelect,
      };
    },
    canSelectMoreOptions() {
      return this.selectedOptions.length < this.question.selectedQuantity;
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.autoFocusField();
    });
  },

  methods: {
    isSelected(id) {
      if (this.settings.isMultiSelect) {
        return this.selectedOptions.includes(id);
      }

      return this.fieldValue === id;
    },
    resetSelection(id) {
      this.selectedOptions = this.selectedOptions.filter((value) => value !== id);
      this.$emit('change', this.selectedOptions);
    },
    selectImage(id) {
      if (this.isSelected(id)) {
        this.resetSelection(id);
        return;
      }

      if (!this.canSelectMoreOptions) {
        return;
      }

      if (this.settings.isMultiSelect) {
        this.selectedOptions.push(id);
        this.$emit('change', this.selectedOptions);
        return;
      }

      this.$emit('change', id);
    },
  },
};
</script>

<style scoped lang="scss">
.RMPE-MultiChoiceImages {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
}
.RMPE-MultiChoiceImage {
  border-radius: 5px;
  width: 48%;
  position: relative;
  margin-bottom: 4%;
  @media screen and (min-width: 768px){
    width: 31%;
  }
  label {
    display: block;
    width: 100%;
    height: 100%;
    border: 1px solid;
    border-radius: 5px;
    transition: transform .3s ease-in-out;
    &:hover,
    &:focus {
      outline: none;
    }
    cursor:pointer!important;
  }

  input {
    display: none;
  }

  .RMPE-MultiChoiceImage-Image {
    width: 100%;
    border-radius: 5px 5px 0 0;
    overflow: hidden;
    padding-bottom: 75%;
    display: block;
    //height: 160px;
    background-repeat: no-repeat;
    background-size: cover;
    background-position: center center;
  }

  .RMPE-MultiChoiceImage-Label {
    display: flex;
    padding: 5px;

    .RMPE-KeyboardKey,
    span {
      margin-right: 15px;
    }
  }

  .RMPE-MultiChoiceImage-Icon {
    position: absolute;
    top: -20px;
    left: -15px;
    font-size: 30px;
    z-index: 2;
  }
}
//.focus-visible {
//  outline: none!important;
//}
</style>
